import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AlertModalComponent, DialogData } from '@app/components/alert-modal/alert-modal.component';
import { Status } from '@interfaces/user';
import DOMPurify from 'dompurify';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class UtilsService {
  constructor(public dialog: MatDialog) {}

  /**
   * Map text css class based on status
   */
  public mapStatusCssClass(status: Status | string): string {
    switch (status) {
      case Status.STATUS_ACTIVE:
        return 'active_text';
      case Status.STATUS_DISABLED:
      case Status.STATUS_INACTIVE:
        return 'inactive_text';
      case Status.STATUS_EMAIL_PENDING:
      case Status.STATUS_PENDING:
        return 'pending_text';
      default:
        return '';
    }
  }

  /**
   * A function that compares two objects and returns the properties that are different.
   * @param obj1 - The first object to compare.
   * @param obj2 - The second object to compare.
   * @returns An object containing the properties that are different.
   */
  getUpdatedProperties = (obj1: any, obj2: any): any => {
    const updatedProperties: any = {};
    for (let key in obj1) {
      if (typeof obj1[key] === 'object' && typeof obj2[key] === 'object') {
        const nestedProperties = this.getUpdatedProperties(obj1[key], obj2[key]);
        if (Object.keys(nestedProperties).length > 0) {
          updatedProperties[key] = nestedProperties;
        }
      } else if (obj1[key] !== obj2[key]) {
        updatedProperties[key] = obj1[key];
      }
    }
    return updatedProperties;
  };

  /**
   * A function that checks if a string is a valid email.
   * @param email - The email to check.
   * @returns A boolean indicating if the email is valid.
   */
  isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  formatDate(date: number | string | undefined | Date, displayFormat: string): string {
    if (!date) return '';
    if (!displayFormat) displayFormat = 'lll';
    return moment(date).format(displayFormat);
  }

  /**
   * A function that generate an RGB color and return it as string.
   * @param minColorRGB - The minimum value for Red, Green and Blue, default: 0.
   * @param maxColorRGB - The maximum value for Red, Green and Blue, default: 255.
   * @returns An string of RGB color in hexadecimal such as #ffffff
   */
  generateRandomColor(minColorRGB: number = 0, maxColorRGB: number = 255): string {
    if (minColorRGB < 0) minColorRGB = 0;
    if (maxColorRGB > 255) maxColorRGB = 255;
    var r = Math.floor(Math.random() * (maxColorRGB - minColorRGB) + minColorRGB);
    var g = Math.floor(Math.random() * (maxColorRGB - minColorRGB) + minColorRGB);
    var b = Math.floor(Math.random() * (maxColorRGB - minColorRGB) + minColorRGB);
    var red = ('0' + r.toString(16)).slice(-2);
    var green = ('0' + g.toString(16)).slice(-2);
    var blue = ('0' + b.toString(16)).slice(-2);
    return '#' + red + green + blue;
  }

  /**
   * A function that shows an alert modal.
   * @param alertData - The data to display in the alert modal.
   */
  showAlert(alertData: DialogData): void {
    const dialogRef = this.dialog.open(AlertModalComponent, {
      data: {
        title: alertData.title,
        alertDescription: alertData.alertDescription,
        alertType: alertData.alertType,
        icon: alertData.icon,
        showActionBtn: alertData.showActionBtn,
        closeText: alertData.closeText,
        actionText: alertData.actionText,
        onAction: alertData.onAction,
        timed: alertData.timed,
        time: alertData.time,
        disableClose: alertData.disableClose,
      },
      disableClose: alertData.disableClose,
      backdropClass: 'disable_close',
    });

    if (alertData.disableClose) {
    }

    if (alertData.timed && !alertData.disableClose) {
      setTimeout(() => {
        dialogRef.close();
      }, alertData.time || 5000);
    }
  }

  /**
   * A function that checks if a URL is valid.
   * @param url - The URL to check.
   * @returns A boolean indicating if the URL is valid.
   */
  isURLValid(url: string): boolean {
    const regex = /^(https?:\/\/)?(([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,})(:\d+)?(\/[a-zA-Z0-9\-._~:\/?#\[\]@!$&'()*+,;=]*)?$/;
    return regex.test(url);
  }

  /**
   * Sanitize HTML content to prevent XSS attacks.
   * @param htmlContent - The HTML content to sanitize.
   * @returns The sanitized HTML content.
   */
  sanitizeHTML(htmlContent: string): string {
    const cleanHtml = DOMPurify.sanitize(htmlContent, {
      ALLOW_DATA_ATTR: true,
      ALLOWED_TAGS: [
        'b',
        'i',
        'em',
        'strong',
        'a',
        'span',
        'ul',
        'ol',
        'li',
        'p',
        'br',
        'pre',
        'code',
        'blockquote',
        'hr',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'img',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
      ],
      ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'class', 'style', 'data-highlight-id', 'id', 'target', 'rel'],
    });
    return cleanHtml;
  }
}
